/**
 * Cut Rite V12 (KOLO 129) — kontrola a odoslanie zákazky.
 *
 * Od KOLA 207 je každá poslaná časť vlastný zoznam v Cut Rite: kontrola,
 * odoslanie, stav, odrezky aj CSV berú výber variant (`varianta_ids`).
 *
 * Odoslanie ide do fronty; pomocník na PC s Cut Rite ho naimportuje
 * a optimalizuje a stav vráti. Kým čaká, posledné odoslanie sa obnovuje,
 * aby user videl „optimalizované" bez F5; od KOLA 145 aj stav píly.
 * Pozri `docs/rozpocet/CUTRITE.md`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  CutriteKontrola,
  CutriteLinka,
  CutriteOdoslanie,
  CutriteOdrezok,
  CutriteOdrezokCreate,
  DataResponse,
} from "@/api/schemas";
import { triggerBlobDownload } from "@/lib/pdfDownload";

/** Import ešte beží — pomocník ho nevzal alebo Cut Rite ešte nedobehol. */
export function beziImport(o: CutriteOdoslanie | null | undefined): boolean {
  return o?.stav === "caka" || o?.stav === "prevzate";
}

/** Prenos na pílu ešte beží (KOLO 145). */
export function beziPila(o: CutriteOdoslanie | null | undefined): boolean {
  return o?.pila_stav === "caka" || o?.pila_stav === "prevzate";
}

/** Tlač nárezových plánov do PDF ešte beží (KOLO 154). */
export function beziPlany(o: CutriteOdoslanie | null | undefined): boolean {
  return o?.plany_stav === "caka" || o?.plany_stav === "prevzate";
}

/** Import alebo píla ešte beží — ďalšie odoslanie by sa im priplietlo. */
export function bezi(o: CutriteOdoslanie | null | undefined): boolean {
  return beziImport(o) || beziPila(o);
}

/** Kľúč výberu do query a adresy — id variant čiarkou (KOLO 207). */
export function kluceVyberu(varianty: readonly string[]): string {
  return varianty.join(",");
}

function parametre(varianty: readonly string[]): { varianta_ids: string } {
  return { varianta_ids: kluceVyberu(varianty) };
}

/** Beží niečo v niektorom zozname — import, píla alebo tlač plánov. */
function beziNieco(o: CutriteOdoslanie | null | undefined): boolean {
  return bezi(o) || beziPlany(o);
}

/**
 * Posledné odoslanie zoznamu pre výber okna (KOLO 145, 207). Kým niečo
 * beží, obnovuje sa každých 10 s. Pri optimalizovanom odoslaní server
 * porovná rezanie so zákazkou (`zmenene`, KOLO 153).
 */
export function useCutritePosledne(
  zakazkaId: string,
  varianty: readonly string[],
  enabled = true,
): UseQueryResult<DataResponse<CutriteOdoslanie | null>> {
  return useQuery({
    queryKey: queryKeys.cutrite.posledne(zakazkaId, kluceVyberu(varianty)),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<CutriteOdoslanie | null>>(
        `/zakazky/${zakazkaId}/cutrite`,
        { params: parametre(varianty) },
      );
      return data;
    },
    enabled: enabled && varianty.length > 0,
    refetchInterval: (q) => (beziNieco(q.state.data?.data) ? 10_000 : false),
  });
}

/**
 * Všetky zoznamy zákazky v Cut Rite (KOLO 207) — každá poslaná časť je
 * vlastný zoznam. Okno ich vymenuje, pás v editore z nich hlási zmenu
 * a hlásenie pri dokončení sleduje každý; preto sa obnovujú, aj keď je
 * okno zatvorené, kým v niektorom niečo beží.
 */
export function useCutriteLinky(
  zakazkaId: string,
): UseQueryResult<DataResponse<CutriteLinka[]>> {
  return useQuery({
    queryKey: queryKeys.cutrite.linky(zakazkaId),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<CutriteLinka[]>>(
        `/zakazky/${zakazkaId}/cutrite/linky`,
      );
      return data;
    },
    refetchInterval: (q) =>
      (q.state.data?.data ?? []).some((l) => beziNieco(l.posledne)) ? 10_000 : false,
  });
}

export function useCutriteKontrola(
  zakazkaId: string,
  varianty: readonly string[],
  enabled: boolean,
): UseQueryResult<DataResponse<CutriteKontrola>> {
  return useQuery({
    queryKey: queryKeys.cutrite.kontrola(zakazkaId, kluceVyberu(varianty)),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<CutriteKontrola>>(
        `/zakazky/${zakazkaId}/cutrite/kontrola`,
        { params: parametre(varianty) },
      );
      return data;
    },
    enabled: enabled && varianty.length > 0,
  });
}

/** Odoslanie zmenilo stav zoznamu — okno ho ukáže hneď, zoznamy načíta znova. */
function useNastavOdoslanie(
  zakazkaId: string,
  varianty: readonly string[],
): (o: CutriteOdoslanie) => void {
  const qc = useQueryClient();
  return (o) => {
    qc.setQueryData(queryKeys.cutrite.posledne(zakazkaId, kluceVyberu(varianty)), {
      data: o,
    });
    void qc.invalidateQueries({ queryKey: queryKeys.cutrite.linky(zakazkaId) });
  };
}

export function usePosliDoCutrite(
  zakazkaId: string,
  varianty: readonly string[],
): UseMutationResult<DataResponse<CutriteOdoslanie>, Error, void> {
  const qc = useQueryClient();
  const nastav = useNastavOdoslanie(zakazkaId, varianty);
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<DataResponse<CutriteOdoslanie>>(
        `/zakazky/${zakazkaId}/cutrite`,
        null,
        { params: parametre(varianty) },
      );
      return data;
    },
    onSuccess: (r) => {
      // Krok „Odoslané" hneď — nečakať na ďalší dotaz
      nastav(r.data);
      void qc.invalidateQueries({
        queryKey: queryKeys.cutrite.kontrola(zakazkaId, kluceVyberu(varianty)),
      });
    },
  });
}

/**
 * Naimportované a optimalizované odoslanie na pílu (KOLO 145). Keď Cut
 * Rite medzitým optimalizoval iný zoznam, server tento pošle znova
 * a odpoveď je nové odoslanie (`pila_z_kancelarie`, KOLO 207).
 */
export function usePosliNaPilu(
  zakazkaId: string,
  varianty: readonly string[],
): UseMutationResult<DataResponse<CutriteOdoslanie>, Error, string> {
  const qc = useQueryClient();
  const nastav = useNastavOdoslanie(zakazkaId, varianty);
  return useMutation({
    mutationFn: async (exportId: string) => {
      const { data } = await api.post<DataResponse<CutriteOdoslanie>>(
        `/zakazky/${zakazkaId}/cutrite/${exportId}/pila`,
      );
      return data;
    },
    onSuccess: (r) => {
      nastav(r.data);
      // Server prepol Ponuku na Vo výrobe a zapísal snímku ceny (KOLO 172) —
      // odznak stavu, zoznam aj História cien to majú ukázať hneď. Nie
      // celé `zakazky.all`: porez a nárez sú ťažké a stav ich nemení.
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
      void qc.invalidateQueries({
        queryKey: queryKeys.zakazky.cenaHistoria(zakazkaId),
      });
    },
  });
}

/**
 * Nárezové plány z Cut Rite do PDF (KOLO 154) — pomocník ich vytlačí
 * a nahrá; smie sa opakovať.
 */
export function useVytlacPlany(
  zakazkaId: string,
  varianty: readonly string[],
): UseMutationResult<DataResponse<CutriteOdoslanie>, Error, string> {
  const nastav = useNastavOdoslanie(zakazkaId, varianty);
  return useMutation({
    mutationFn: async (exportId: string) => {
      const { data } = await api.post<DataResponse<CutriteOdoslanie>>(
        `/zakazky/${zakazkaId}/cutrite/${exportId}/plany`,
      );
      return data;
    },
    onSuccess: (r) => nastav(r.data),
  });
}

/**
 * Odrezok pre zoznam (KOLO 178, od KOLA 207 pre výber variant) — pomocník
 * ho pri odoslaní pridá do Seznamu tabulí úlohy. Kontrola nesie zoznam aj
 * „+ 1 odrezok"; posledné odoslanie `zmenene` (odrezok po optimalizácii =
 * poslať znova).
 */
function useObnovPoOdrezku(zakazkaId: string, varianty: readonly string[]): () => void {
  const qc = useQueryClient();
  return () => {
    const vyber = kluceVyberu(varianty);
    void qc.invalidateQueries({ queryKey: queryKeys.cutrite.kontrola(zakazkaId, vyber) });
    void qc.invalidateQueries({ queryKey: queryKeys.cutrite.posledne(zakazkaId, vyber) });
    void qc.invalidateQueries({ queryKey: queryKeys.cutrite.linky(zakazkaId) });
  };
}

export function usePridajOdrezok(
  zakazkaId: string,
  varianty: readonly string[],
): UseMutationResult<DataResponse<CutriteOdrezok>, Error, CutriteOdrezokCreate> {
  const obnov = useObnovPoOdrezku(zakazkaId, varianty);
  return useMutation({
    mutationFn: async (payload: CutriteOdrezokCreate) => {
      const { data } = await api.post<DataResponse<CutriteOdrezok>>(
        `/zakazky/${zakazkaId}/cutrite/odrezky`,
        payload,
        { params: parametre(varianty) },
      );
      return data;
    },
    onSuccess: obnov,
  });
}

export function useZmazOdrezok(
  zakazkaId: string,
  varianty: readonly string[],
): UseMutationResult<void, Error, string> {
  const obnov = useObnovPoOdrezku(zakazkaId, varianty);
  return useMutation({
    mutationFn: async (odrezokId: string) => {
      await api.delete(`/zakazky/${zakazkaId}/cutrite/odrezky/${odrezokId}`);
    },
    onSuccess: obnov,
  });
}

/** Ten istý súbor na stiahnutie — kým pomocník na PC nebeží. */
export async function stiahniCutriteCsv(
  zakazkaId: string,
  varianty: readonly string[],
  nazovSuboru: string,
): Promise<void> {
  const r = await api.get<Blob>(`/zakazky/${zakazkaId}/cutrite.csv`, {
    params: parametre(varianty),
    responseType: "blob",
  });
  triggerBlobDownload(r.data, nazovSuboru, "text/csv");
}
